import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  findBusinesses as findBusinessesApi,
  runGraderScan as runGraderScanApi,
  fetchBusinessByPlaceId as fetchBusinessByPlaceIdApi,
  verifyUnlock as verifyUnlockApi,
  startUnlockOtp as startUnlockOtpApi,
  checkUnlockOtp as checkUnlockOtpApi,
  submitProspectInfo as submitProspectInfoApi
} from '../api/graderApi';
import { PILLAR_LABELS, getDeltaTone, getScoreTone } from '../utils/formatters';
import { SCAN_ACTIVITY_LABELS, SCAN_STEP_MIN_DURATION_MS, SCAN_STEPS } from '../model/constants';
import { getTurnstileToken } from '../../../utils/turnstile';

const HONEYPOT_INPUT_SELECTOR = 'input[data-honeypot="grader"]';
const readHoneypot = () => {
  if (typeof document === 'undefined') return '';
  const el = document.querySelector(HONEYPOT_INPUT_SELECTOR);
  return el && typeof el.value === 'string' ? el.value : '';
};

export const useGraderFlow = () => {
  const location = useLocation();

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [phase, setPhase] = useState('search');
  const [scanStepIndex, setScanStepIndex] = useState(-1);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [scanId, setScanId] = useState('');
  const [unlockToken, setUnlockToken] = useState('');
  const [preview, setPreview] = useState(null);
  const [fullReport, setFullReport] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockStep, setUnlockStep] = useState('phone');
  const [unlockPhone, setUnlockPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpStatusMessage, setOtpStatusMessage] = useState('');
  const [otpResendCooldownMs, setOtpResendCooldownMs] = useState(0);
  const [unlockError, setUnlockError] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpChecking, setOtpChecking] = useState(false);
  const [prospectSubmitting, setProspectSubmitting] = useState(false);
  const [prospectName, setProspectName] = useState('');
  const [prospectEmail, setProspectEmail] = useState('');
  const [prospectRelationship, setProspectRelationship] = useState('owner_manager');
  const [prospectOptIn, setProspectOptIn] = useState(false);
  const [prospectConsentAccepted, setProspectConsentAccepted] = useState(false);
  const [scanTelemetry, setScanTelemetry] = useState({
    recordsAnalyzed: 0,
    localEntities: 0,
    rankingSignals: 0,
    confidence: 0
  });
  const [isCompactMobile, setIsCompactMobile] = useState(() => window.innerWidth <= 768);
  const [showScanDetails, setShowScanDetails] = useState(false);
  const [previewRevealStage, setPreviewRevealStage] = useState(0);
  // Bootstrap business resolved from URL params (?placeid=/?query=) but NOT
  // auto-scanned. Requires explicit user click to start. Closes the
  // spammable-URL hole that previously let any URL trigger a paid scan.
  const [pendingBootstrapBusiness, setPendingBootstrapBusiness] = useState(null);
  const [pendingBootstrapError, setPendingBootstrapError] = useState('');

  const wrapperRef = useRef(null);
  const searchTimerRef = useRef(null);
  const bootstrapKeyRef = useRef('');

  const attributionParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      placeid: params.get('placeid') || params.get('placeId') || '',
      lp: params.get('lp') || '',
      name: params.get('name') || '',
      city: params.get('city') || ''
    };
  }, [location.search]);

  const findBusinesses = useCallback((searchText, timeoutMs = 8000) => (
    findBusinessesApi(searchText, timeoutMs)
  ), []);

  const runGraderScan = useCallback(async (business, timeoutMs = 60000) => {
    const turnstileToken = await getTurnstileToken();
    return runGraderScanApi({
      business,
      attribution: attributionParams,
      turnstileToken,
      honeypot: readHoneypot(),
      timeoutMs
    });
  }, [attributionParams]);

  const fetchBusinessByPlaceId = useCallback((placeId, timeoutMs = 10000) => (
    fetchBusinessByPlaceIdApi(placeId, timeoutMs)
  ), []);

  const verifyUnlock = useCallback((scanIdValue, tokenValue, timeoutMs = 15000) => (
    verifyUnlockApi(scanIdValue, tokenValue, timeoutMs)
  ), []);

  const startUnlockOtp = useCallback((payload, timeoutMs = 15000) => (
    startUnlockOtpApi(payload, timeoutMs)
  ), []);

  const checkUnlockOtp = useCallback((payload, timeoutMs = 15000) => (
    checkUnlockOtpApi(payload, timeoutMs)
  ), []);

  const submitProspectInfo = useCallback((payload, timeoutMs = 15000) => (
    submitProspectInfoApi(payload, timeoutMs)
  ), []);

  const startScan = useCallback(async (business) => {
    setSelectedBusiness(business);
    setPhase('scanning');
    setScanStepIndex(0);
    setSearchError('');
    setPreview(null);
    setFullReport(null);
    setScanId('');
    setUnlockToken('');
    setUnlocking(false);
    setUnlockStep('phone');
    setUnlockPhone('');
    setOtpCode('');
    setOtpStatusMessage('');
    setOtpResendCooldownMs(0);
    setUnlockError('');
    setOtpSending(false);
    setOtpChecking(false);
    setProspectSubmitting(false);
    setProspectName('');
    setProspectEmail('');
    setProspectRelationship('owner_manager');
    setProspectOptIn(false);
    setProspectConsentAccepted(false);
    setShowScanDetails(false);

    try {
      const scanPromise = runGraderScan(business);
      let scanResult = null;
      let scanError = null;

      scanPromise
        .then((result) => {
          scanResult = result;
        })
        .catch((error) => {
          scanError = error;
        });

      for (let i = 0; i < SCAN_STEPS.length; i += 1) {
        setScanStepIndex(i);
        const minimumStepMs = SCAN_STEP_MIN_DURATION_MS[i] || 1800;
        await new Promise((resolve) => setTimeout(resolve, minimumStepMs));
      }

      while (!scanResult && !scanError) {
        setScanStepIndex(SCAN_STEPS.length - 1);
        await new Promise((resolve) => setTimeout(resolve, 900));
      }

      if (scanError) {
        throw scanError;
      }

      const discoveryMeta = scanResult?.preview?.dataQuality?.discoveryMeta;
      if (discoveryMeta) {
        setScanTelemetry({
          recordsAnalyzed: Number(discoveryMeta.totalCandidates || 0),
          localEntities: Number(discoveryMeta.relevantCandidates || 0),
          rankingSignals: Number(discoveryMeta.queriesExecuted || 0) * 12,
          confidence: discoveryMeta.usedAgent ? 92 : 78
        });
      }
      setScanId(scanResult.scanId);
      setUnlockToken(scanResult.unlockToken || '');
      setPreview(scanResult.preview);
      setPhase('preview');
    } catch (error) {
      console.error('Scan failure:', error);
      setPhase('error');
      setSearchError('Scan timed out while gathering deep competitor data. Please try again or use a broader business query.');
    }
  }, [runGraderScan]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const onSelectBusiness = (event) => {
      if (!event?.detail) {
        return;
      }
      const selected = event.detail;
      setQuery(`${selected.name || ''}${selected.city ? ` ${selected.city}` : ''}`.trim());
      setShowDropdown(false);
      setSuggestions([]);
      startScan(selected);
    };

    window.addEventListener('selectBusiness', onSelectBusiness);
    return () => window.removeEventListener('selectBusiness', onSelectBusiness);
  }, [startScan]);

  useEffect(() => {
    const uniqueKey = `${location.search}|${location.state?.selectedBusiness?.id || ''}`;
    if (bootstrapKeyRef.current === uniqueKey) {
      return;
    }
    bootstrapKeyRef.current = uniqueKey;

    // Same-origin click handoff (e.g. home-page CompetitionSearchLite passes
    // selectedBusiness via router state). This is a real user click that just
    // happened, so auto-scanning is safe.
    const stateBusiness = location.state?.selectedBusiness;
    if (stateBusiness) {
      const text = `${stateBusiness.name || ''}${stateBusiness.city ? ` ${stateBusiness.city}` : ''}`.trim();
      setQuery(text);
      setShowDropdown(false);
      setSuggestions([]);
      setLoadingSuggestions(false);
      setPendingBootstrapBusiness(null);
      setPendingBootstrapError('');
      startScan(stateBusiness);
      return;
    }

    // URL-param bootstraps (?placeid=, ?query=) are spammable — anyone can
    // craft a link that drives traffic to /competition-report?placeid=XYZ. We
    // resolve the business so we can show "Run scan for [name]?" but require
    // an explicit user click to actually spend money.
    const placeIdFromQuery = attributionParams.placeid;
    if (placeIdFromQuery) {
      const fallbackName = attributionParams.name;
      const fallbackCity = attributionParams.city;

      if (fallbackName || fallbackCity) {
        setQuery(`${fallbackName || ''}${fallbackCity ? ` ${fallbackCity}` : ''}`.trim());
      }

      setLoadingSuggestions(true);
      setShowDropdown(false);
      setSearchError('');
      setPendingBootstrapError('');

      let cancelled = false;
      const runPlaceBootstrap = async () => {
        try {
          const resolvedBusiness = await fetchBusinessByPlaceId(placeIdFromQuery);
          if (cancelled || !resolvedBusiness) {
            return;
          }

          setQuery(`${resolvedBusiness.name || fallbackName || ''}${resolvedBusiness.city ? ` ${resolvedBusiness.city}` : (fallbackCity ? ` ${fallbackCity}` : '')}`.trim());
          setPendingBootstrapBusiness(resolvedBusiness);
        } catch (error) {
          if (!cancelled) {
            console.error('PlaceId bootstrap error:', error);
            setPendingBootstrapError('Could not resolve this Google listing. Try searching by business name and city.');
          }
        } finally {
          if (!cancelled) {
            setLoadingSuggestions(false);
          }
        }
      };

      runPlaceBootstrap();

      return () => {
        cancelled = true;
      };
    }

    const presetQuery = new URLSearchParams(location.search).get('query') || '';
    if (presetQuery.trim().length < 3) {
      return;
    }

    setQuery(presetQuery);
    setLoadingSuggestions(true);
    setShowDropdown(false);
    setSearchError('');
    setPendingBootstrapError('');

    let cancelled = false;
    const hardStop = setTimeout(() => {
      if (cancelled) {
        return;
      }
      setLoadingSuggestions(false);
      setPendingBootstrapError('Search took too long. Please try again.');
    }, 10000);

    const run = async () => {
      try {
        const results = await findBusinesses(presetQuery);
        if (cancelled) {
          return;
        }
        setSuggestions(results);
        if (results.length > 0) {
          // Pre-select the top result for the user to confirm; do NOT auto-scan.
          setPendingBootstrapBusiness(results[0]);
        } else {
          setPendingBootstrapError('No businesses matched. Try a broader search.');
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Preset search error:', error);
          setSuggestions([]);
          setPendingBootstrapError('Could not load search results.');
        }
      } finally {
        if (!cancelled) {
          setLoadingSuggestions(false);
        }
        clearTimeout(hardStop);
      }
    };

    run();

    return () => {
      cancelled = true;
      clearTimeout(hardStop);
    };
  }, [location.search, location.state, findBusinesses, startScan, attributionParams, fetchBusinessByPlaceId]);

  const confirmPendingBootstrap = useCallback(async () => {
    if (!pendingBootstrapBusiness) return;
    const business = pendingBootstrapBusiness;
    setPendingBootstrapBusiness(null);
    setPendingBootstrapError('');
    await startScan(business);
  }, [pendingBootstrapBusiness, startScan]);

  const cancelPendingBootstrap = useCallback(() => {
    setPendingBootstrapBusiness(null);
    setPendingBootstrapError('');
  }, []);

  const handleSearchChange = (value) => {
    setQuery(value);
    setSearchError('');
    if (pendingBootstrapBusiness) {
      setPendingBootstrapBusiness(null);
      setPendingBootstrapError('');
    }

    if (phase !== 'search') {
      setPhase('search');
      setSelectedBusiness(null);
      setPreview(null);
      setFullReport(null);
      setScanId('');
      setUnlockToken('');
      setScanStepIndex(-1);
    }

    if (value.trim().length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      setLoadingSuggestions(false);
      return;
    }

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    setLoadingSuggestions(true);
    setShowDropdown(true);

    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await findBusinesses(value);
        setSuggestions(results);
      } catch (error) {
        console.error('Business search error:', error);
        setSuggestions([]);
        setSearchError('Search timed out. Please try again.');
      } finally {
        setLoadingSuggestions(false);
      }
    }, 400);
  };

  const onSelectSuggestion = async (business) => {
    setQuery(`${business.name || ''}${business.city ? ` ${business.city}` : ''}`.trim());
    setShowDropdown(false);
    setSuggestions([]);
    await startScan(business);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (query.trim().length < 3) {
      return;
    }
    // Honeypot — silently no-op if a bot filled the hidden field.
    if (readHoneypot()) {
      return;
    }
    if (suggestions.length > 0) {
      await onSelectSuggestion(suggestions[0]);
      return;
    }

    setLoadingSuggestions(true);
    try {
      const results = await findBusinesses(query);
      setSuggestions(results);
      if (results.length > 0) {
        await onSelectSuggestion(results[0]);
      } else {
        setSearchError('No businesses found. Try a broader search.');
      }
    } catch (error) {
      console.error('Submit search error:', error);
      setSearchError('Search failed. Please try again.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const pillarCards = useMemo(() => {
    if (!preview?.subscores) {
      return [];
    }
    return Object.entries(preview.subscores).map(([key, value]) => ({
      key,
      label: PILLAR_LABELS[key] || key,
      value
    }));
  }, [preview]);

  const scanProgress = useMemo(() => {
    if (phase !== 'scanning' || scanStepIndex < 0) {
      return 0;
    }
    return Math.max(8, Math.round(((scanStepIndex + 1) / SCAN_STEPS.length) * 100));
  }, [phase, scanStepIndex]);

  useEffect(() => {
    const onResize = () => setIsCompactMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const currentScanStepLabel = useMemo(() => {
    if (scanStepIndex < 0 || scanStepIndex >= SCAN_STEPS.length) {
      return 'Preparing scan...';
    }
    return SCAN_STEPS[scanStepIndex];
  }, [scanStepIndex]);

  const mapEmbedUrl = useMemo(() => {
    if (!selectedBusiness) {
      return '';
    }

    const lat = Number(selectedBusiness.lat);
    const lng = Number(selectedBusiness.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) {
      return `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}&z=15&output=embed`;
    }

    const fallbackQuery = `${selectedBusiness.name || ''} ${selectedBusiness.address || ''} ${selectedBusiness.city || ''}`.trim();
    return `https://www.google.com/maps?q=${encodeURIComponent(fallbackQuery)}&z=14&output=embed`;
  }, [selectedBusiness]);

  const scanActivity = useMemo(() => {
    const label = SCAN_ACTIVITY_LABELS[scanStepIndex % SCAN_ACTIVITY_LABELS.length] || SCAN_ACTIVITY_LABELS[0];
    const cityText = selectedBusiness?.city && selectedBusiness.city !== 'N/A' ? selectedBusiness.city : 'local area';
    return [
      label,
      `Cross-checking competitors in ${cityText}`,
      'Computing confidence from live coverage'
    ];
  }, [scanStepIndex, selectedBusiness]);

  const mapZoomTransform = useMemo(() => {
    const progress = phase === 'scanning' ? Math.max(0, Math.min(scanProgress, 100)) : 0;
    const scale = (1 + (progress * 0.0022)).toFixed(3);
    const offsetY = (progress * 0.02).toFixed(2);
    return `scale(${scale}) translate3d(0, -${offsetY}%, 0)`;
  }, [phase, scanProgress]);

  useEffect(() => {
    if (phase !== 'scanning') {
      setScanTelemetry({
        recordsAnalyzed: 0,
        localEntities: 0,
        rankingSignals: 0,
        confidence: 0
      });
      return;
    }

    const targets = [
      { recordsAnalyzed: 50, localEntities: 8, rankingSignals: 26, confidence: 22 },
      { recordsAnalyzed: 140, localEntities: 16, rankingSignals: 44, confidence: 32 },
      { recordsAnalyzed: 240, localEntities: 24, rankingSignals: 68, confidence: 46 },
      { recordsAnalyzed: 340, localEntities: 34, rankingSignals: 90, confidence: 60 },
      { recordsAnalyzed: 430, localEntities: 44, rankingSignals: 108, confidence: 72 },
      { recordsAnalyzed: 510, localEntities: 52, rankingSignals: 124, confidence: 84 },
      { recordsAnalyzed: 580, localEntities: 60, rankingSignals: 140, confidence: 96 }
    ];

    const activeTarget = targets[Math.max(0, Math.min(scanStepIndex, targets.length - 1))];

    const interval = setInterval(() => {
      setScanTelemetry((current) => ({
        recordsAnalyzed: Math.min(activeTarget.recordsAnalyzed, current.recordsAnalyzed + Math.ceil(Math.random() * 10)),
        localEntities: Math.min(activeTarget.localEntities, current.localEntities + (Math.random() > 0.6 ? 1 : 0)),
        rankingSignals: Math.min(activeTarget.rankingSignals, current.rankingSignals + Math.ceil(Math.random() * 4)),
        confidence: Math.min(activeTarget.confidence, current.confidence + 1)
      }));
    }, 360);

    return () => clearInterval(interval);
  }, [phase, scanStepIndex]);

  const unlockFullReport = useCallback(async () => {
    if (!scanId) {
      setSearchError('Scan session missing. Please run a new scan.');
      setPhase('error');
      return;
    }

    setUnlocking(true);
    setSearchError('');
    setUnlockError('');

    try {
      const report = await verifyUnlock(scanId, unlockToken || undefined);
      setFullReport(report);
      setPhase('full');
    } catch (error) {
      console.error('Unlock failure:', error);
      setUnlockError('Could not unlock report yet. Please verify your details and try again.');
      setPhase('error');
    } finally {
      setUnlocking(false);
    }
  }, [scanId, unlockToken, verifyUnlock]);

  const beginUnlockFlow = useCallback(() => {
    setUnlockStep('phone');
    setUnlockError('');
    setOtpStatusMessage('');
    setOtpResendCooldownMs(0);
    setPhase('booking');
  }, []);

  const handleSendOtp = useCallback(async () => {
    if (!scanId || !unlockToken) {
      setUnlockError('Scan session missing. Please run a new scan.');
      return;
    }

    if (!unlockPhone.trim()) {
      setUnlockError('Please enter a phone number.');
      return;
    }

    if (otpResendCooldownMs > 0) {
      return;
    }

    setOtpSending(true);
    setUnlockError('');

    try {
      const result = await startUnlockOtp({
        scanIdValue: scanId,
        tokenValue: unlockToken,
        phoneValue: unlockPhone.trim()
      });

      setOtpStatusMessage(`Versa Launch code sent to ${result?.phoneMasked || 'your phone'}.`);
      setOtpResendCooldownMs(Number(result?.retryAfterMs || 0));
      setUnlockStep('otp');
    } catch (error) {
      if (Number(error?.retryAfterMs || 0) > 0) {
        setOtpResendCooldownMs(Number(error.retryAfterMs));
      }
      setUnlockError(error.message || 'Could not send code. Please try again.');
    } finally {
      setOtpSending(false);
    }
  }, [scanId, unlockToken, unlockPhone, otpResendCooldownMs, startUnlockOtp]);

  const handleVerifyOtp = useCallback(async () => {
    if (!otpCode.trim()) {
      setUnlockError('Please enter the verification code.');
      return;
    }

    setOtpChecking(true);
    setUnlockError('');

    try {
      await checkUnlockOtp({
        scanIdValue: scanId,
        tokenValue: unlockToken,
        phoneValue: unlockPhone.trim(),
        codeValue: otpCode.trim()
      });

      setOtpStatusMessage('Phone verified successfully for Versa Launch.');
      setUnlockStep('prospect');
    } catch (error) {
      setUnlockError(error.message || 'Invalid code. Please try again.');
    } finally {
      setOtpChecking(false);
    }
  }, [otpCode, scanId, unlockToken, unlockPhone, checkUnlockOtp]);

  const handleSubmitProspect = useCallback(async () => {
    if (!prospectName.trim()) {
      setUnlockError('Please enter your name.');
      return;
    }

    if (!prospectEmail.trim()) {
      setUnlockError('Please enter your email.');
      return;
    }

    if (!prospectConsentAccepted) {
      setUnlockError('Please confirm consent to unlock the report.');
      return;
    }

    setProspectSubmitting(true);
    setUnlockError('');

    try {
      await submitProspectInfo({
        scanIdValue: scanId,
        tokenValue: unlockToken,
        name: prospectName.trim(),
        email: prospectEmail.trim(),
        relationship: prospectRelationship,
        optIn: prospectOptIn,
        consentAccepted: prospectConsentAccepted
      });

      await unlockFullReport();
    } catch (error) {
      setUnlockError(error.message || 'Could not submit your details. Please try again.');
    } finally {
      setProspectSubmitting(false);
    }
  }, [
    prospectName,
    prospectEmail,
    prospectRelationship,
    prospectOptIn,
    prospectConsentAccepted,
    scanId,
    unlockToken,
    submitProspectInfo,
    unlockFullReport
  ]);

  const resetAll = () => {
    setPhase('search');
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
    setSelectedBusiness(null);
    setPreview(null);
    setFullReport(null);
    setScanId('');
    setUnlockToken('');
    setUnlocking(false);
    setUnlockStep('phone');
    setUnlockPhone('');
    setOtpCode('');
    setOtpStatusMessage('');
    setOtpResendCooldownMs(0);
    setUnlockError('');
    setOtpSending(false);
    setOtpChecking(false);
    setProspectSubmitting(false);
    setProspectName('');
    setProspectEmail('');
    setProspectRelationship('owner_manager');
    setProspectOptIn(false);
    setProspectConsentAccepted(false);
    setScanStepIndex(-1);
    setSearchError('');
    setPendingBootstrapBusiness(null);
    setPendingBootstrapError('');
  };

  useEffect(() => {
    if (otpResendCooldownMs <= 0) {
      return undefined;
    }

    const timer = setInterval(() => {
      setOtpResendCooldownMs((current) => Math.max(0, current - 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [otpResendCooldownMs]);

  const otpCooldownSeconds = Math.ceil(otpResendCooldownMs / 1000);

  const visibleScanActivity = isCompactMobile ? scanActivity.slice(0, 2) : scanActivity;
  const scanConfidencePct = Math.max(6, Math.min(100, Number(scanTelemetry.confidence || 0)));

  const pillarComparisons = useMemo(() => {
    const entries = Object.entries(fullReport?.subscores || {});
    return entries.map(([key, value]) => {
      const cohortValue = fullReport?.competitorAverage?.pillars?.[key];
      const hasCohort = cohortValue !== undefined && cohortValue !== null;
      const delta = hasCohort ? Number(value || 0) - Number(cohortValue || 0) : 0;
      const tone = hasCohort ? getDeltaTone(delta) : getScoreTone(value);
      return {
        key,
        label: PILLAR_LABELS[key] || key,
        value,
        cohortValue,
        hasCohort,
        delta,
        tone
      };
    });
  }, [fullReport]);

  const atRiskPillars = useMemo(
    () => pillarComparisons.filter((item) => item.tone !== 'strong'),
    [pillarComparisons]
  );

  const winningPillars = useMemo(
    () => pillarComparisons.filter((item) => item.tone === 'strong'),
    [pillarComparisons]
  );

  useEffect(() => {
    if (phase !== 'preview') {
      setPreviewRevealStage(0);
      return;
    }

    setPreviewRevealStage(1);
    const first = setTimeout(() => setPreviewRevealStage(2), 180);
    const second = setTimeout(() => setPreviewRevealStage(3), 420);
    const third = setTimeout(() => setPreviewRevealStage(4), 680);

    return () => {
      clearTimeout(first);
      clearTimeout(second);
      clearTimeout(third);
    };
  }, [phase, preview?.generatedAt]);

  return {
    query,
    setQuery,
    suggestions,
    showDropdown,
    setShowDropdown,
    loadingSuggestions,
    searchError,
    phase,
    scanStepIndex,
    selectedBusiness,
    preview,
    fullReport,
    unlocking,
    unlockStep,
    unlockPhone,
    setUnlockPhone,
    otpCode,
    setOtpCode,
    otpStatusMessage,
    otpResendCooldownMs,
    unlockError,
    otpSending,
    otpChecking,
    prospectSubmitting,
    prospectName,
    setProspectName,
    prospectEmail,
    setProspectEmail,
    prospectRelationship,
    setProspectRelationship,
    prospectOptIn,
    setProspectOptIn,
    prospectConsentAccepted,
    setProspectConsentAccepted,
    scanTelemetry,
    isCompactMobile,
    showScanDetails,
    setShowScanDetails,
    previewRevealStage,
    wrapperRef,
    handleSearchChange,
    onSubmit,
    onSelectSuggestion,
    currentScanStepLabel,
    scanProgress,
    mapEmbedUrl,
    mapZoomTransform,
    visibleScanActivity,
    scanConfidencePct,
    scanActivity,
    pillarCards,
    beginUnlockFlow,
    handleSendOtp,
    otpCooldownSeconds,
    handleVerifyOtp,
    handleSubmitProspect,
    atRiskPillars,
    winningPillars,
    resetAll,
    SCAN_STEPS,
    pendingBootstrapBusiness,
    pendingBootstrapError,
    confirmPendingBootstrap,
    cancelPendingBootstrap
  };
};

export default useGraderFlow;